import { Box, Divider, Group, ScrollArea, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"

import { StatusDot } from "@tfg/ui"

import type {
  EcoModulationImportSummaryDto,
  EcoModulationImportValidationErrorDto,
} from "~/gen/api"

type EcoModulationImportSummaryProps = {
  readonly summary: EcoModulationImportSummaryDto
  readonly validationErrors: readonly EcoModulationImportValidationErrorDto[]
}

const IDENTITY_COLUMN_COUNT = 4
const DATAPOINT_CODE_COUNT = 5
/** Fired-only template: identity + one `_fired` column per datapoint (no sourcePhrase). */
const TEMPLATE_COLUMN_COUNT = IDENTITY_COLUMN_COUNT + DATAPOINT_CODE_COUNT

export const EcoModulationImportSummary = ({
  summary,
  validationErrors,
}: EcoModulationImportSummaryProps) => {
  const { t } = useTranslation("dataTransfer")
  const hasErrors = validationErrors.length > 0
  const errorColor = hasErrors ? "var(--mantine-color-error-text)" : undefined

  return (
    <Stack
      gap="var(--tfg-molecule-padding-y)"
      p="var(--tfg-molecule-padding-y) var(--tfg-molecule-padding-x)"
      bd="1px solid var(--mantine-color-gray-2)"
      bdrs="var(--tfg-card-border-radius)"
      bg="var(--mantine-color-white)"
    >
      <Stack gap={2}>
        <Text size="sm" fw={600}>
          {t("import.ecoModulation.summaryModal.lineDetails")}
        </Text>
        <Text size="xs" c="dimmed">
          {t("import.ecoModulation.summaryModal.lineDetailsMeta", {
            rowCount: summary.totalRows,
            columnCount: TEMPLATE_COLUMN_COUNT,
          })}
        </Text>
      </Stack>

      <Divider color="var(--mantine-color-gray-2)" />

      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <StatusDot tone="success" />
          <Text size="sm">
            {t("import.ecoModulation.summaryModal.validLines")}
          </Text>
        </Group>
        <Text size="sm">{summary.validRows}</Text>
      </Group>

      <Group wrap="nowrap" align="flex-start" gap="xs">
        <Box mt={6}>
          <StatusDot tone="error" />
        </Box>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c={errorColor}>
              {t("import.ecoModulation.summaryModal.errorLines")}
            </Text>
            <Text size="sm" c={errorColor}>
              {summary.erroredRows}
            </Text>
          </Group>
          {hasErrors && (
            <ScrollArea.Autosize mah={176} type="hover">
              <Stack gap={4}>
                {validationErrors.map((error, index) => (
                  <Group
                    key={`${error.rowNumber}-${error.message}-${index}`}
                    wrap="nowrap"
                    align="flex-start"
                    gap="xs"
                  >
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      ↳
                    </Text>
                    {error.rowNumber > 0 && (
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {t("import.ecoModulation.summaryModal.lineNumber", {
                          rowNumber: error.rowNumber,
                        })}
                      </Text>
                    )}
                    <Text size="xs">{error.message}</Text>
                  </Group>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>
      </Group>

      <Divider color="var(--mantine-color-gray-2)" />

      <Group justify="space-between" wrap="nowrap">
        <Text size="sm" fw={700}>
          {t("import.ecoModulation.summaryModal.totalImportable")}
        </Text>
        <Text size="sm" fw={700}>
          {summary.validRows}
        </Text>
      </Group>
    </Stack>
  )
}
